import React from "react";
import { AppHeader } from "./CommunitySelection";
import { useLang } from "../lib/i18n";
import { ArrowRight, Heart } from "lucide-react";

const POINTS = [
  "You are this neighbor. Choose only from the options and knowledge they actually have — not from your own income, education, savings, credit, car, or network of people who could help.",
  "Set aside your own instincts. Things you might reach for — dipping into savings, comparing prices, negotiating, borrowing from family — may simply not be available to them.",
  "Your goal is not to win, find the cheapest option, or save money. It is to feel how limited resources and competing responsibilities shape everyday decisions.",
  "There are no wrong answers. Within scarcity, people are usually making the best decision available to them.",
];

export const InstructionsScreen = ({ onContinue, quick = false }) => {
  const { t } = useLang();
  return (
    <div className="min-h-screen">
      <AppHeader subtitle={t("Before you begin")} />
      <div className="max-w-2xl mx-auto px-5 sm:px-6 py-10 sm:py-14">
        <div className="animate-rise">
          <div className="inline-flex items-center gap-2 text-vin bg-vin/5 px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.14em] font-semibold">
            <Heart className="w-3.5 h-3.5" strokeWidth={2} /> {t("Walk in their shoes")}
          </div>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold text-ink leading-tight">
            {t("A word before you start")}
          </h2>
          <p className="mt-4 text-base text-ink-soft leading-relaxed">
            {quick
              ? t("You are about to step into a few pivotal moments in the life of a neighbor in your community — and see how just a couple of decisions can set off consequences that reach far beyond them. This experience is designed to challenge our assumptions.")
              : t("You are about to walk through a year in the life of a neighbor in your community. This experience is designed to challenge our assumptions.")}
          </p>
        </div>

        <div className="mt-6 bg-vin text-white rounded-lg p-6 sm:p-7 animate-rise">
          <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white/70">
            {t("The one thing to remember")}
          </p>
          <p className="mt-2 font-display text-xl sm:text-2xl font-semibold leading-snug">
            {t("Step aside from your own life. For the next few minutes, walk in the shoes of your neighbor — and decide only from what they can see, reach, and afford.")}
          </p>
        </div>

        <ul className="mt-8 space-y-4">
          {POINTS.map((p, i) => (
            <li
              key={i}
              className="bg-white rounded-lg border border-[var(--line)] p-5 flex items-start gap-3 animate-rise"
              style={{ animationDelay: `${i * 70}ms` }}
            >
              <span className="mt-1 h-6 w-6 rounded-full bg-vin/10 text-vin flex items-center justify-center text-sm font-semibold shrink-0">
                {i + 1}
              </span>
              <span className="text-sm text-ink leading-relaxed">{t(p)}</span>
            </li>
          ))}
        </ul>

        <button
          data-testid="instructions-continue-button"
          onClick={onContinue}
          className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-vin text-white font-semibold px-8 py-4 rounded-full hover:bg-vin-hover transition-colors duration-200"
        >
          {t("I'm ready — choose a community")}
          <ArrowRight className="w-5 h-5" strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
};

export default InstructionsScreen;
